import { useEffect, useLayoutEffect, useCallback, useState } from 'react';
import { useGameState } from '../context/GameStateContext';

/**
 * Custom hook for responsive layout calculations
 * Ports the logic from the demo HTML to handle wide/narrow mode switching
 * and dynamic board sizing
 */
export const useResponsiveLayout = () => {
  const [isNarrow, setIsNarrow] = useState(false);
  const [cssLoaded, setCssLoaded] = useState(false);
  const { gameData } = useGameState();
  const gridSize = gameData?.puzzle?.size;
  
  const CONFIG = {
    gridSize,
    minCellPx: 40,   // minimum cell px from demo
  };

  const readPxVar = useCallback((name) => {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    const m = raw.match(/([0-9.]+)/);
    return m ? parseFloat(m[1]) : 0;
  }, []);

  const px = useCallback((n) => `${Math.max(0, Math.round(n))}px`, []);

  // Check if CSS custom properties are loaded and available
  const checkCssLoaded = useCallback(() => {
    const root = document.documentElement;
    const topbarH = getComputedStyle(root).getPropertyValue('--topbar-h').trim();
    const navbarH = getComputedStyle(root).getPropertyValue('--navbar-h').trim();
    const narrowPanelH = getComputedStyle(root).getPropertyValue('--narrowPanel-h').trim();

    // If we can read at least these critical CSS variables, CSS is loaded
    return topbarH && navbarH && narrowPanelH;
  }, []);

  const updateLayout = useCallback(() => {
    if (!CONFIG.gridSize) {
      return { wide: true, boardSize: 400, scrolling: false };
    }

    // Don't calculate layout if CSS isn't loaded yet
    if (!cssLoaded) {
      console.log('Layout calculation skipped - CSS not loaded yet');
      return { wide: true, boardSize: 400, scrolling: false };
    }

    const root = document.documentElement;
    // Look for the game container using data attribute
    const gameContainer = document.querySelector('[data-game-container="true"]');

    if (!gameContainer) {
      return { wide: true, boardSize: 400, scrolling: false };
    }

    const topbarH = readPxVar('--topbar-h');
    const navbarH = readPxVar('--navbar-h');
    const gap = readPxVar('--between');

    const sideW = readPxVar('--sidePanel-w');
    const modeH = readPxVar('--modePanel-h');
    const narrowH = readPxVar('--narrowPanel-h');

    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;

    // Inner container dimensions (no padding since we removed it)
    const containerH = vh - topbarH - navbarH;
    const containerW = vw;

    // Simplified: Use hardcoded 640px breakpoint for now
    const wide = vw >= 640;

    // Toggle classes on game container - use raw class names since we can't import CSS modules here
    gameContainer.classList.toggle('narrow', !wide);
    gameContainer.classList.remove('narrowScroll');
    
    // Also try with CSS module compatible approach
    if (!wide) {
      gameContainer.classList.add('narrow');
    } else {
      gameContainer.classList.remove('narrow');
    }
    
    // Update state
    setIsNarrow(!wide);
    
    // Debug logging
    console.log(`Layout: vw=${vw}, wide=${wide}, narrow=${!wide}, classes=${gameContainer.className}`);
    console.log('Should add narrow class:', !wide, 'Current classes:', Array.from(gameContainer.classList));

    let boardSize;

    if (wide) {
      // Height-driven: maximize grid height, let side panels scale accordingly
      const verticalRoom = containerH - modeH - gap;
      const horizontalRoom = containerW - (2 * sideW) - (2 * gap);
      // Prioritize vertical space in landscape mode
      boardSize = Math.min(verticalRoom, horizontalRoom);
    } else {
      // Stacked: mode + board + (value bottom bar) + (action bottom bar) + 3 gaps
      const fixedStack = modeH + (2 * narrowH) + (3 * gap);
      const verticalRoom = containerH - fixedStack;
      const minBoardByCells = CONFIG.gridSize * CONFIG.minCellPx;

      if (verticalRoom >= minBoardByCells) {
        boardSize = Math.min(containerW, verticalRoom);
      } else {
        gameContainer.classList.add('narrowScroll');      // allow scrolling to preserve fixed bars
        boardSize = Math.min(containerW, minBoardByCells);
      }
    }

    // Set the board size CSS custom property
    root.style.setProperty('--board-size', px(boardSize));

    return { wide, boardSize, scrolling: gameContainer.classList.contains('narrowScroll') };
  }, [CONFIG.gridSize, CONFIG.minCellPx, readPxVar, px, cssLoaded]);

  // Check for CSS loading on mount
  useEffect(() => {
    const checkAndSetCss = async () => {
      // Wait for fonts to be ready (indicates stylesheets are loaded)
      if (document.fonts) {
        await document.fonts.ready;
      }

      // Double-check CSS custom properties are available
      let attempts = 0;
      const maxAttempts = 10;

      const checkInterval = setInterval(() => {
        attempts++;
        if (checkCssLoaded() || attempts >= maxAttempts) {
          clearInterval(checkInterval);
          setCssLoaded(true);
          console.log('CSS loaded and ready, attempts:', attempts);
        }
      }, 50); // Check every 50ms
    };

    checkAndSetCss();
  }, [checkCssLoaded]);

  // Use useLayoutEffect for synchronous layout updates after DOM mutations
  useLayoutEffect(() => {
    if (!cssLoaded) return;

    // Initial layout calculation
    updateLayout();

    // Add resize listeners
    const handleResize = () => updateLayout();

    window.addEventListener('resize', handleResize, { passive: true });
    window.addEventListener('orientationchange', handleResize, { passive: true });

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, [updateLayout, cssLoaded]);

  return { updateLayout, isNarrow, cssLoaded };
};